/**
 * Shared styles for Radix UI DropdownMenu components.
 *
 * These styles provide consistent appearance across all dropdown menus
 * in the application (UserControls, DevToolsMenu, future menus).
 *
 * Usage:
 *   import { dropdownStyles } from '@/lib/dropdownStyles';
 *
 *   <DropdownMenu.Content className={dropdownStyles.content}>
 *     <DropdownMenu.Item className={dropdownStyles.item}>...</DropdownMenu.Item>
 *     <DropdownMenu.Separator className={dropdownStyles.separator} />
 *     <DropdownMenu.Label className={dropdownStyles.label}>...</DropdownMenu.Label>
 *   </DropdownMenu.Content>
 */

/**
 * Core dropdown menu styles - use these class strings directly
 */
export const dropdownStyles = {
  /** Menu content container */
  content: 'min-w-[200px] bg-surface-2 rounded-md shadow-lg border border-border p-1 z-50',

  /** Standard menu item (navigation, action) */
  item: 'px-3 py-2 text-sm outline-none cursor-pointer rounded text-foreground hover:bg-muted/20 focus:bg-muted/20',

  /** Menu item with icon/indicator (checkbox, radio) - adds flex layout */
  itemWithIndicator: 'px-3 py-2 text-sm outline-none cursor-pointer rounded text-foreground hover:bg-muted/20 focus:bg-muted/20 flex items-center gap-2',

  /** Danger/destructive item (logout, delete) */
  itemDanger: 'px-3 py-2 text-sm outline-none cursor-pointer rounded text-danger hover:bg-muted/20 focus:bg-muted/20',

  /** Horizontal separator between sections */
  separator: 'h-px bg-border my-1',

  /** Section label/header */
  label: 'px-3 py-2 text-xs text-muted font-medium',

  /** Item indicator container (for checkbox/radio marks) */
  indicator: 'w-4 h-4 inline-flex items-center justify-center',

  /** Radio indicator dot */
  radioIndicator: 'w-2 h-2 rounded-full bg-primary',

  /** Checkbox indicator checkmark */
  checkIndicator: 'text-primary',
} as const;

/**
 * Helper to get item text margin based on selection state.
 * Used for checkbox/radio items where text needs to shift when indicator is hidden.
 *
 * @param isSelected - Whether the item is currently selected
 * @returns Tailwind margin class
 */
export function getIndicatorMargin(isSelected: boolean): string {
  return isSelected ? 'ml-0' : 'ml-6';
}
